import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { usePost } from '../../contexts/PostContext';
import { useComment } from '../../contexts/CommentContext';
import { FaThumbsUp, FaThumbsDown, FaReply, FaEdit, FaTrash, FaRegComment } from 'react-icons/fa';
import PostList from '../../components/PostList';
import { postAPI } from '../../api';

const PostDetailContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 0;
`;

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const PostListContainer = styled.div`
  margin-top: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  padding: 1rem;
`;

const PostContainer = styled.div`
  width: 100%;
  padding: 2rem 1rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
`;

const PostHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background-color: #f8f9fa;
  padding: 0.5rem 1.5rem;
  border-radius: 8px;
  /* box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05); */
`;

const PostTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  color: #333;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: 700;
`;

const PostMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.secondary};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const PostMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 1rem;
  color: #555;
  font-size: 13px;
  
  &:last-child {
    margin-right: 0;
  }
`;

const PostContent = styled.div`
  font-size: 16px;
  line-height: 1.8;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: white;
  border-radius: 8px;
  /* box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08); */
  min-height: 300px;
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1rem 0;
  }
  
  p {
    margin-bottom: 1.2rem;
  }
  
  h1, h2, h3, h4, h5, h6 {
    margin-top: 2rem;
    margin-bottom: 1rem;
    font-weight: 600;
    color: #333;
  }
  
  h1 { font-size: 24px; }
  h2 { font-size: 22px; }
  h3 { font-size: 20px; }
  h4 { font-size: 18px; }
  h5 { font-size: 16px; }
  h6 { font-size: 15px; }
  
  ul, ol {
    padding-left: 2rem;
    margin-bottom: 1.2rem;
  }
  
  blockquote {
    border-left: 4px solid ${({ theme }) => theme.colors.primary};
    padding: 1rem 1.5rem;
    font-style: italic;
    margin: 1.5rem 0;
    background-color: #f8f9fa;
    border-radius: 0 4px 4px 0;
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: 8px;
  background-color: #f8f9fa;
  /* box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05); */
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #555;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.light};
    border-color: #ccc;
  }
`;

const EditButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ffffff;
  background-color: #4dabf7;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: 4px;
  margin-right: ${({ theme }) => theme.spacing.sm};
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;

  &:hover {
    background-color: #339af0;
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ffffff;
  background-color: #fa5252;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: 4px;
  border: none;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #e03131;
  }
`;

const VoteContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const VoteButton = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: ${props => props.active ? 'white' : '#555'};
  background-color: ${props => props.active ? props.color || '#339af0' : 'white'};
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? props.color || '#228be6' : '#f1f3f5'};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
`;

const CommentsSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const CommentsSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  /* box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05); */
`;

const CommentsTitle = styled.h2`
  font-size: 18px;
  margin: 0;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CommentOptions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const CommentOptionButton = styled.button<{ active?: boolean }>`
  font-size: 14px;
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.secondary};
  background: ${props => props.active ? '#e7f5ff' : 'none'};
  border: ${props => props.active ? `1px solid ${props.theme.colors.primary}` : '1px solid #ced4da'};
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: ${props => props.active ? '600' : '400'};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background-color: #f1f3f5;
  }
`;

const CommentForm = styled.form`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  background-color: white;
  padding: 1.2rem;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
`;

const CommentTextarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: vertical;
  min-height: 100px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-family: inherit;
  font-size: 15px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(51, 154, 240, 0.1);
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 4px;
  font-weight: 500;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #228be6;
  }
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const CommentItem = styled.div`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #eee;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
  }
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

const CommentAuthor = styled.span`
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 14px;
  
  img {
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }
`;

const CommentDate = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
`;

const CommentContent = styled.p`
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  color: #333;
  white-space: pre-line;
  padding: 0.5rem 0;
  word-break: break-word;
`;

const CommentActions = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-top: ${({ theme }) => theme.spacing.sm};
  border-top: 1px solid #f1f3f5;
  padding-top: 0.5rem;
`;

const CommentAction = styled.button`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background-color: #f1f3f5;
  }
`;

const ReplyList = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  margin-left: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    left: -12px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #e9ecef;
    border-radius: 1px;
  }
`;

const ReplyItem = styled(CommentItem)`
  background-color: #f8f9fa;
  padding: ${({ theme }) => theme.spacing.md};
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: -12px;
    top: 15px;
    width: 12px;
    height: 2px;
    background-color: #e9ecef;
  }
`;

const ReplyForm = styled.form`
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: 8px;
  background-color: #f8f9fa;
  border: 1px dashed #e9ecef;
  
  ${CommentTextarea} {
    min-height: 80px;
    background-color: white;
  }
  
  ${SubmitButton} {
    font-size: 13px;
  }
`;

const ReplyButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: white;
  color: #666;
  border-radius: 4px;
  font-weight: 500;
  font-size: 14px;
  border: 1px solid #ced4da;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f1f3f5;
  }
`;

const NeedLogin = styled.div`
  text-align: center; 
  padding: 1.5rem; 
  margin-bottom: 1rem; 
  background: #f8f9fa; 
  border-radius: 8px;
  color: #666;
  font-size: 14px;
  border: 1px solid #e9ecef;

  p{
    margin-bottom: 0;
  }
  
  a {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const EmptyComments = styled.div`
  text-align: center;
  padding: 2.5rem;
  color: #868e96;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #e9ecef;
  font-size: 15px;
`;

const LoadingComments = styled.div`
  text-align: center;
  padding: 2rem;
  color: #868e96;
`;

const PostDetail: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [commentContent, setCommentContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [editingComment, setEditingComment] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [sortByLatest, setSortByLatest] = useState(true);
  const [post, setPost] = useState<any>(null);

  // 인증 컨텍스트 사용
  const { isAuthenticated, user } = useAuth();
  
  // 게시물 컨텍스트 사용
  const { 
    getList,
    loading: postLoading, 
    error: postError, 
    fetchPostById,
    fetchPostListByBoard
  } = usePost();

  const { list, pagination } =  useMemo(()=>{
    if(post && post.board){
      return getList(post.board.id);
    }
    return {list: [], pagination: {currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10}};
  }, [post, getList]);
  
  // 댓글 컨텍스트 사용
  const { 
    comments, 
    loading: commentLoading, 
    error: commentError, 
    getCommentsByPostId, 
    createComment, 
    updateComment, 
    deleteComment 
  } = useComment();

  useEffect(()=>{
    if(post && post.board){
      fetchPostListByBoard(post.board.id, 1, 10);
    }
  },[post])

  // 게시물 및 댓글 로드
  useEffect(() => {
    if (!postId) return;
    
    const loadPostAndComments = async () => {
      try {
        const fetchedPost = await fetchPostById(Number(postId));
        if (fetchedPost) {
          setPost(fetchedPost);
          // 게시물 로드 후 댓글 로드
          await getCommentsByPostId(Number(postId));
        }
      } catch (error) {
        console.error('게시물 및 댓글 로딩 오류:', error);
      }
    };
    
    loadPostAndComments();
  }, [postId, fetchPostById, getCommentsByPostId]);

  // 댓글 작성 처리
  const handleCommentSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentContent.trim() || !postId || !isAuthenticated) return;

    try {
      await createComment(Number(postId), commentContent);
      setCommentContent('');
      // 댓글 작성 후 목록 새로고침
      await getCommentsByPostId(Number(postId));
    } catch (error) {
      console.error('댓글 작성 오류:', error);
    }
  }, [commentContent, postId, isAuthenticated, createComment, getCommentsByPostId]);

  // 대댓글 작성 처리
  const handleReplySubmit = useCallback(async (e: React.FormEvent, parentId: number) => {
    e.preventDefault();
    if (!replyContent.trim() || !postId || !isAuthenticated) return;

    try {
      await createComment(Number(postId), replyContent, parentId);
      setReplyContent('');
      setReplyingTo(null);
      // 대댓글 작성 후 목록 새로고침
      await getCommentsByPostId(Number(postId));
    } catch (error) {
      console.error('대댓글 작성 오류:', error);
    }
  }, [replyContent, postId, isAuthenticated, createComment, getCommentsByPostId]);

  // 댓글 수정 처리
  const handleCommentEdit = useCallback(async (e: React.FormEvent, commentId: number) => {
    e.preventDefault();
    if (!editContent.trim() || !isAuthenticated) return;

    try {
      await updateComment(commentId, editContent);
      setEditingComment(null);
      setEditContent('');
      // 댓글 수정 후 목록 새로고침
      await getCommentsByPostId(Number(postId));
    } catch (error) {
      console.error('댓글 수정 오류:', error);
    }
  }, [editContent, isAuthenticated, updateComment, getCommentsByPostId]);

  // 댓글 삭제 처리
  const handleCommentDelete = useCallback(async (commentId: number) => {
    if (!window.confirm('정말로 댓글을 삭제하시겠습니까?') || !isAuthenticated) return;

    try {
      await deleteComment(commentId);
      // 댓글 삭제 후 목록 새로고침
      await getCommentsByPostId(Number(postId));
    } catch (error) {
      console.error('댓글 삭제 오류:', error);
    }
  }, [isAuthenticated, deleteComment, getCommentsByPostId]);

  // 게시물 삭제 처리
  const handleDeletePost = useCallback(async () => {
    if (!window.confirm('정말로 게시글을 삭제하시겠습니까?')) return;
    if (!postId || !post) return;

    try {
      await postAPI.deletePost(Number(postId));
      // 삭제 후 게시판 목록으로 이동
      navigate(`/boards/${post.board.id}`);
    } catch (error) {
      console.error('게시글 삭제 오류:', error);
    }
  }, [postId, post, navigate]);

  // 게시글 좋아요
  const handleLikePost = useCallback(async () => {
    if (!postId || !isAuthenticated) return;

    try {
      // 좋아요 기능이 현재 Context에 없어서 직접 구현해야 함
      // TODO: likePost 기능 구현 필요
      console.log('좋아요 기능 구현 필요');
    } catch (error) {
      console.error('게시글 좋아요 오류:', error);
    }
  }, [postId, isAuthenticated]);

  // 작성자 여부 확인
  const isAuthor = post && user?.id === post.author.id;

  // 댓글 정렬
  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) => {
      if (sortByLatest) {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else {
        return (b.likeCount || 0) - (a.likeCount || 0);
      }
    });
  }, [comments, sortByLatest]);

  if (postLoading) {
    return (
      <PostDetailContainer>
        <RightContent>
          <PostContainer>
            <PostHeader>
              <PostTitle>게시글을 불러오는 중...</PostTitle>
            </PostHeader>
          </PostContainer>
        </RightContent>
      </PostDetailContainer>
    );
  }

  if (postError) {
    return (
      <PostDetailContainer>
        <RightContent>
          <PostContainer>
            <PostHeader>
              <PostTitle>게시글을 불러오는 중 오류가 발생했습니다: {postError}</PostTitle>
            </PostHeader>
          </PostContainer>
        </RightContent>
      </PostDetailContainer>
    );  
  }

  if (!post) {
    return (
      <PostDetailContainer>
        <RightContent>
          <PostContainer>
            <PostHeader>
              <PostTitle>게시글을 찾을 수 없습니다.</PostTitle>
            </PostHeader>
          </PostContainer>
        </RightContent>
      </PostDetailContainer>
    );
  }
  

  return (
    <PostDetailContainer>
      <RightContent>
        <PostContainer>
          <PostHeader>
            <PostTitle>{post?.title}</PostTitle>
            <PostMeta>
              <div>
                <PostMetaItem>작성자: {post?.author?.username}</PostMetaItem>
                <PostMetaItem>작성일: {post && post.created_at ? new Date(post.created_at).toLocaleString() : '날짜 정보 없음'}</PostMetaItem>
              </div>
              <div>
                <PostMetaItem>조회수: {post?.view_count || 0}</PostMetaItem>
                <PostMetaItem>댓글: {post?.commentCount || 0}</PostMetaItem>
                <PostMetaItem>
                  {post && <Link to={`/boards/${post.board.id}`}>{post.board.name}</Link>}
                </PostMetaItem>
              </div>
            </PostMeta>
          </PostHeader>

          <PostContent dangerouslySetInnerHTML={{ __html: post?.content || '' }} />

          <ActionsContainer>
            <div>
              {post && <BackLink to={`/boards/${post.board.id}`}>목록으로</BackLink>}
            </div>
            
            <VoteContainer>
              <VoteButton 
                onClick={handleLikePost} 
                active={false} 
                color="#339af0"
              >
                <FaThumbsUp size={16} /> 좋아요 {post?.likeCount || 0}
              </VoteButton>
              {/* 비활성화된 싫어요 버튼 - 기능 추가 시 활성화 */}
              <VoteButton color="#fa5252">
                <FaThumbsDown size={16} /> 싫어요 0
              </VoteButton>
            </VoteContainer>
            
            {isAuthor && (
              <ButtonGroup>
                {post && (
                  <EditButton to={`/posts/${post.id}/edit`}>
                    <FaEdit size={16} /> 수정
                  </EditButton>
                )}
                <DeleteButton onClick={handleDeletePost}>
                  <FaTrash size={16} /> 삭제
                </DeleteButton>
              </ButtonGroup>
            )}
          </ActionsContainer>

          <CommentsSection>
            <CommentsSectionHeader>
              <CommentsTitle>
                <FaRegComment size={16} /> 댓글 ({comments.length})
              </CommentsTitle>
              <CommentOptions>
                <CommentOptionButton 
                  active={sortByLatest} 
                  onClick={() => setSortByLatest(true)}
                >
                  최신순
                </CommentOptionButton>
                <CommentOptionButton 
                  active={!sortByLatest} 
                  onClick={() => setSortByLatest(false)}
                >
                  추천순
                </CommentOptionButton>
              </CommentOptions>
            </CommentsSectionHeader>
            
            {commentLoading ? (
              <LoadingComments>
                댓글을 불러오는 중...
              </LoadingComments>
            ) : (
              <CommentList>
                {!isAuthenticated && (
                  <NeedLogin>
                    <p>댓글을 작성하려면 <Link to="/login">로그인</Link>이 필요합니다.</p>
                  </NeedLogin>
                )}
                
                {isAuthenticated && (
                  <CommentForm onSubmit={handleCommentSubmit}>
                    <CommentTextarea
                      value={commentContent}
                      onChange={(e) => setCommentContent(e.target.value)}
                      placeholder="댓글을 작성해주세요."
                      required
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <SubmitButton type="submit">
                        <FaRegComment size={16} /> 댓글 등록
                      </SubmitButton>
                    </div>
                  </CommentForm>
                )}
                
                {comments.length === 0 ? (
                  <EmptyComments>
                    아직 댓글이 없습니다. 첫 댓글을 작성해보세요!
                  </EmptyComments>
                ) : (
                  sortedComments.map(comment => (
                    <CommentItem key={comment.id}>
                      <CommentHeader>
                        <CommentAuthor>
                          {comment.username || '사용자'}
                        </CommentAuthor>
                        <CommentDate>{new Date(comment.createdAt).toLocaleString()}</CommentDate>
                      </CommentHeader>
                      
                      {editingComment === comment.id ? (
                        <ReplyForm onSubmit={(e) => handleCommentEdit(e, comment.id)}>
                          <CommentTextarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            required
                          />
                          <ReplyButtonGroup>
                            <CancelButton 
                              type="button"
                              onClick={() => {
                                setEditingComment(null);
                                setEditContent('');
                              }}
                            >
                              취소
                            </CancelButton>
                            <SubmitButton type="submit">
                              <FaEdit size={16} /> 수정
                            </SubmitButton>
                          </ReplyButtonGroup>
                        </ReplyForm>
                      ) : (
                        <CommentContent>{comment.content}</CommentContent>
                      )}
                      
                      {isAuthenticated && editingComment !== comment.id && (
                        <CommentActions>
                          <CommentAction onClick={() => setReplyingTo(comment.id)}>
                            <FaReply size={14} /> 답글
                          </CommentAction>
                          {(user?.id === comment.userId) && (
                            <>
                              <CommentAction onClick={() => {
                                setEditingComment(comment.id);
                                setEditContent(comment.content);
                              }}>
                                <FaEdit size={14} /> 수정
                              </CommentAction>
                              <CommentAction onClick={() => handleCommentDelete(comment.id)}>
                                <FaTrash size={14} /> 삭제
                              </CommentAction>
                            </>
                          )}
                        </CommentActions>
                      )}
                      
                      {replyingTo === comment.id && (
                        <ReplyForm onSubmit={(e) => handleReplySubmit(e, comment.id)}>
                          <CommentTextarea
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            placeholder="답글을 작성해주세요."
                            required
                          />
                          <ReplyButtonGroup>
                            <CancelButton 
                              type="button"
                              onClick={() => setReplyingTo(null)}
                            >
                              취소
                            </CancelButton>
                            <SubmitButton type="submit">
                              <FaReply size={16} /> 답글 등록
                            </SubmitButton>
                          </ReplyButtonGroup>
                        </ReplyForm>
                      )}
                      
                      {comment.replies && comment.replies.length > 0 && (
                        <ReplyList>
                          {comment.replies.map(reply => (
                            <ReplyItem key={reply.id}>
                              <CommentHeader>
                                <CommentAuthor>
                                  {reply.username || '사용자'}
                                </CommentAuthor>
                                <CommentDate>{new Date(reply.createdAt).toLocaleString()}</CommentDate>
                              </CommentHeader>
                              
                              {editingComment === reply.id ? (
                                <ReplyForm onSubmit={(e) => handleCommentEdit(e, reply.id)}>
                                  <CommentTextarea
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    required
                                  />
                                  <ReplyButtonGroup>
                                    <CancelButton 
                                      type="button"
                                      onClick={() => {
                                        setEditingComment(null);
                                        setEditContent('');
                                      }}
                                    >
                                      취소
                                    </CancelButton>
                                    <SubmitButton type="submit">
                                      <FaEdit size={16} /> 수정
                                    </SubmitButton>
                                  </ReplyButtonGroup>
                                </ReplyForm>
                              ) : (
                                <CommentContent>{reply.content}</CommentContent>
                              )}
                              
                              {isAuthenticated && editingComment !== reply.id && 
                                (user?.id === reply.userId) && (
                                <CommentActions>
                                  <CommentAction onClick={() => {
                                    setEditingComment(reply.id);
                                    setEditContent(reply.content);
                                  }}>
                                    <FaEdit size={14} /> 수정
                                  </CommentAction>
                                  <CommentAction onClick={() => handleCommentDelete(reply.id)}>
                                    <FaTrash size={14} /> 삭제
                                  </CommentAction>
                                </CommentActions>
                              )}
                            </ReplyItem>
                          ))}
                        </ReplyList>
                      )}
                    </CommentItem>
                  ))
                )}
              </CommentList>
            )}
          </CommentsSection>
        </PostContainer>
        
        {post && post.board && (
          <PostListContainer>
            <PostList 
              list={list}
              pagination={pagination}
              boardId={post.board.id} 
              title={`${post.board.name} 게시판의 다른 글`}
              customPageHandler={(page) => fetchPostListByBoard(post.board.id, page, 10)}
              showWriteButton={false}
              showSearch={false}
            />
          </PostListContainer>
        )}
      </RightContent>
    </PostDetailContainer>
  );
};

export default PostDetail;